import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import CircularProgress from '@material-ui/core/CircularProgress';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBackIos';

import GroupSimpleSelect from 'app/components/group/SimpleSelect';
import Loading from 'app/components/loaders/Loading';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    groupLink: {
        verticalAlign: 'inherit'
    },
    groupSelect: {
        marginBottom: theme.spacing(3)
    },
    input: {
        display: 'none',
    },
}));

const initValues = {
    id: 0,
    group_id: null,
    name: ``,
    folder_id: 0
}

export default function EditFolderDialog(props) {

    const { app } = useApp();
    const classes = useStyles();
    const api = useApi();

    const {
        initFolder,
        initGroups,
        initGroupsLoading,
        initGroupId,
        folderId,
        open,
        onClose,
        onSave,
        onClosed
    } = props;

    const [ values, setValues ] = useState(initValues);

    const [ step, setStep ] = useState(1);
    const [ groups, setGroups ] = useState([]);

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ groupsLoading, setGroupsLoading ] = useState(false);

    // Getter Functions -----------------------------------------------

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/admin/group')
            .then( res => setGroups(res.groups))
            .finally(() => setGroupsLoading(false));
    };

    useEffect(() => {
        if (initGroups) {
            setGroups(initGroups);
        } else {
            getGroups();
        }
    }, [initGroups]);

    useEffect(() => {
        const newValues = {...values};

        if ((typeof initGroupId !== 'undefined') && !initFolder) {
            newValues.group_id = initGroupId;
        }

        newValues.folder_id = folderId ?? 0;

        setValues(newValues);

    }, [initGroupId, folderId]);

    useEffect(() => {
        setGroupsLoading(initGroupsLoading);
    }, [initGroupsLoading]);

    useEffect(() => {
        if (initFolder) setValues(initFolder);
    }, [initFolder]);

    const selectedGroup = useMemo(() => {
        return groups.find(group => group.id === values.group_id) ?? {title: 'Admin Only'};
    }, [groups, values.group_id]);

    const membersGroup = useMemo(() => {
        if (groups.length) {
            return groups.find(group => group.membership_level == true && group.members_level == true && group.parent_id === 0);
        }
        return {id: 0}
    }, [groups]);

    // Setter Functions -----------------------------------------------

    const handleFolderName = e => {
        const value = e.target.value;

        if (isError('name')) delete errors.message;

        setValues({ ...values, name: value });
    };

    const handleGroupId = id => {
        if (isError('group_id')) delete errors.group_id;
        setValues({ ...values, group_id: id });
    };

    const toggleGroupId = () => {
        if (isError('group_id')) delete errors.group_id;
        setValues({ ...values, group_id: values.group_id === null ?  membersGroup.id : null});
    }

    const handleStep = (e,s) => {
        e.preventDefault();
        setStep(s);
    };

    // Post Functions -----------------------------------------------

    const postFolder = () => {
        api.post('/admin/folder/' + values.id, values)
            .then(() => onSave())
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const onExited = () => {

        if (typeof initGroupId !== 'undefined') {
            setValues({...initValues, group_id: initGroupId});
        } else {
            setValues({...initValues, group_id: membersGroup.id});
        }

        setStep(1);
        setLoading(false);
        setGroupsLoading(false);
        setErrors({});

        if (onClosed) onClosed();
    }

    const groupLinkToolTipText = () => {
        if (values.group_id === null) return "Folder is admin only";
        else if (values.group_id === 0) return "Viewable to everyone in the Club";
        else return "Viewable to " + selectedGroup.title;
    }

    return (
        <Dialog
            aria-labelledby="edit-folder-dialog-title"
            open={open}
            aria-describedby="edit-folder-dialogue-description"
            fullWidth
            maxWidth="xs"
            disableBackdropClick={(step === 3 || loading)}
            disableEscapeKeyDown={(step === 3 || loading)}
            onClose={onClose}
            onExited={onExited}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-folder-dialog-title">
                {(step === 1) &&
                    <Fragment>
                        {(values.id > 0) ? "Editing " + values.name : "Create a New Folder"}
                        <IconButton
                            aria-label="Cancel"
                            className={classes.closeButton}
                            onClick={onClose}
                            disabled={loading}
                            >
                            <CloseIcon />
                        </IconButton>
                    </Fragment>
                }
                {step === 2 &&
                    <Button
                        startIcon={<LeftIcon/>}
                        aria-label="Back"
                        onClick={(e) => handleStep(e,1)}
                        >
                        Back to Upload
                    </Button>
                }
            </DialogTitle>
            <DialogContent>
                {(step === 1) &&
                    <Fragment>
                        <div className={classes.groupSelect}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.group_id === null}
                                        onChange={toggleGroupId}
                                        name="admin_only"
                                        color="primary"
                                    />
                                }
                                label="Admin Only"
                            />
                            {values.group_id !== null &&
                                <Typography variant="subtitle2">
                                    <Tooltip
                                        title={groupLinkToolTipText()}
                                        aria-label={groupLinkToolTipText()}
                                        >
                                        <span>
                                            <Link
                                                className={classes.groupLink}
                                                variant="subtitle2"
                                                component="button"
                                                onClick={(e) => handleStep(e,2)}
                                                color="primary"
                                                disabled={values.group_id === null}
                                                >
                                                {values.group_id === 0 ? app.club.name : selectedGroup.title}
                                            </Link>
                                        </span>
                                    </Tooltip>
                                    <span>&nbsp;have access to this folder.</span>
                                </Typography>
                            }
                        </div>
                        <TextField
                            id="input-folder-name"
                            label="Folder Name"
                            value={values.name}
                            onChange={handleFolderName}
                            fullWidth
                            required
                            variant="outlined"
                            margin="normal"
                            error={isError('name')}
                            helperText={getErrorMessage('name')}
                        />
                    </Fragment>
                }
                {(step === 2) &&
                    <Fragment>
                        <Typography variant="h6">Select a Group</Typography>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            This group and any of its child groups will have access to this folder and all of its contents. This will overwrite any group settings on files and folders within this folder.
                        </Typography>
                        <GroupSimpleSelect
                            color="primary"
                            initSelectedId={values.group_id}
                            initGroupOptions={groups}
                            onSelect={handleGroupId}
                            optionsLoading={groupsLoading}
                        />
                    </Fragment>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    onClick={postFolder}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
